/**
 * Contrato de CCG do tomador (P-TOM-C3).
 *
 * Arquivo proprio do contrato, ao lado do tomador: o contrato tem rotas proprias
 * (`/policyholders/{id}/ccg-contract`) e entra na ficha por uma secao nova. Ver
 * docs/ADR/0063-ccg-do-tomador.md.
 */

import type { Money } from './comum';

import { api } from '../client';

export type PolicyholderCcgStatus =
  'rascunho' | 'em_assinatura' | 'assinado' | 'cancelado';

export interface PolicyholderCcgContract {
  id: string;
  number: number;
  status: PolicyholderCcgStatus;
  status_description: string;
  notice_kind: 'sucesso' | 'alerta' | 'erro';
  valid_from: string | null;
  valid_until: string | null;
  /** Teto informado. Regra sem fonte: nao decide cobertura. */
  ceiling: Money | null;
  notes: string;
  signed_at: string | null;
  cancel_reason: string;
  cancelled_at: string | null;
  cancelled_label: string;
  created_label: string;
  created_at: string;
  updated_at: string;
  coverage: { decided: boolean; covered: boolean; rule: string; reason: string };
}

export interface PolicyholderCcgVersion {
  version: number;
  kind: 'gerada' | 'assinada';
  filename: string;
  byte_size: number;
  checksum: string;
  has_pdf: boolean;
  template_source: string;
  template_name: string;
  template_version: number | null;
  parties_count: number;
  created_label: string;
  created_at: string;
}

export interface PolicyholderCcgEvent {
  id: string;
  kind: string;
  message: string;
  actor_label: string;
  occurred_at: string;
}

export interface PolicyholderCcgSigner {
  position: number;
  name: string;
  role: string;
  masked_document: string;
  status: 'pendente' | 'assinado' | 'recusado';
  decided_at: string | null;
}

export interface PolicyholderCcgSignature {
  id: string;
  number: number;
  status: string;
  open: boolean;
  provider: string;
  provider_name: string;
  deadline_at: string;
  sent_at: string | null;
  finished_at: string | null;
  failure_message: string;
  signers: PolicyholderCcgSigner[];
}

export interface PolicyholderCcgParty {
  position: number;
  party_id: string | null;
  role: 'representante_legal' | 'fiador_pf' | 'fiador_pj';
  signer_role: string;
  name: string;
  masked_document: string;
  on_behalf_of: string;
  signature_status: 'pendente' | 'assinado' | 'recusado';
  decided_at: string | null;
}

/** Contrato cancelado do tomador, com as versoes para baixar pelo contrato. */
export interface PolicyholderCcgPrevious {
  id: string;
  number: number;
  status: string;
  cancel_reason: string;
  cancelled_at: string | null;
  signed_at: string | null;
  created_at: string;
  versions: PolicyholderCcgVersion[];
}

export interface PolicyholderCcgDetail {
  contract: PolicyholderCcgContract | null;
  versions: PolicyholderCcgVersion[];
  events: PolicyholderCcgEvent[];
  signature_request: PolicyholderCcgSignature | null;
  parties: PolicyholderCcgParty[];
  previous: PolicyholderCcgPrevious[];
  can_sign: boolean;
  can_sign_reason: string;
  can_update: boolean;
  can_cancel: boolean;
  pdf_available: boolean;
}

export interface PolicyholderCcgInput {
  valid_from: string | null;
  valid_until: string | null;
  ceiling: Money | null;
  notes: string;
}

export interface PolicyholderCcgDownload {
  url: string;
  filename: string;
  format: 'pdf' | 'docx';
  version: number;
  kind: 'gerada' | 'assinada';
  expires_at: string;
}

function contratoDo(id: string, resto = ''): string {
  return `/api/v1/policyholders/${id}/ccg-contract${resto}`;
}

/** Contrato de CCG do tomador. */
export const policyholderCcg = {
  get: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderCcgDetail>(contratoDo(id), signal),
  create: (id: string, input: PolicyholderCcgInput) =>
    api.post<PolicyholderCcgDetail>(contratoDo(id), input),
  update: (id: string, input: PolicyholderCcgInput) =>
    api.patch<PolicyholderCcgDetail>(contratoDo(id), input),
  generate: (id: string, templateId?: string | null) =>
    api.post<PolicyholderCcgDetail>(
      contratoDo(id, '/document'),
      templateId ? { template_id: templateId } : {},
    ),
  /**
   * Endereco temporario de uma versao. `contractId` escolhe um contrato anterior do
   * tomador; sem ele, o contrato em uso.
   */
  download: (
    id: string,
    version?: number,
    format?: 'pdf' | 'docx',
    contractId?: string,
  ) => {
    const parametros = new URLSearchParams();
    if (contractId) parametros.set('contract_id', contractId);
    if (version) parametros.set('version', String(version));
    if (format) parametros.set('format', format);
    const consulta = parametros.toString();
    return api.get<PolicyholderCcgDownload>(
      contratoDo(id, `/document${consulta ? `?${consulta}` : ''}`),
    );
  },
  sign: (
    id: string,
    input: { include_insurer_signer: boolean; message: string; deadline_days: number },
  ) => api.post<PolicyholderCcgDetail>(contratoDo(id, '/signature'), input),
  reopen: (id: string) =>
    api.post<PolicyholderCcgDetail>(contratoDo(id, '/reopening'), {}),
  cancel: (id: string, reason: string) =>
    api.post<PolicyholderCcgDetail>(contratoDo(id, '/cancellation'), { reason }),
};
